import React, { useRef, useState, useEffect } from 'react';
import { Box } from '@mui/material';

import 'react-quill/dist/quill.snow.css';

//import DOMPurify from 'dompurify';

import {
  IChecklist,
  IDelta,
  IForm,
  IPromptCard,
  ITextBlock,
} from '@homeporter/types';

import ChecklistBlock from './ChecklistBlock';
import { useAIDocumentEditorContext } from '../../../../contexts/AIDocumentEditorContext';
import { useSessionContext } from '../../../../contexts/SessionContext';
import { useBookingsContext } from '../../../../contexts/BookingsContext';

import { useEditableBlocksStyles } from './styles';

import SectionBlock from './SectionBlock';
import GenerateReport from './GenerateReport';

import FormBlock from './FormBlock';
import PromptBlock from './PromptBlock';
import TextBlock from './TextBlock';

const DocumentRenderer = () => {
  const styles = useEditableBlocksStyles();

  const { selectedBooking: booking, description } = useBookingsContext();

  const {
    documentsRef,
    documentIndex,
    documentSection,
    setDocumentSection,
    setDocumentSections,
    setSelectedCard,
    selectedCard,
    setSaveEnabled,
    showGenerateReportPanel,
    documentRef,
    quillRef,
    setRegenerateIndex,
    isGeneratingResponse,
    activeTab,
    setActiveTab,
    highlightedSids,
    setHighlightedSids,
    setHighlightSentence,
    regenerateIndex,
  } = useAIDocumentEditorContext();

  const { setSelectedImage, imagesMapRef } = useSessionContext();

  const [wordCount, setWordCount] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<string>(''); // get default from booking
  const [editingTitleIndex, setEditingTitleIndex] = useState<number | null>(
    null,
  );

  const characterLimit = 500;

  const handleSelectCard = (event: React.MouseEvent<HTMLDivElement>) => {
    console.log('::::handleSelectCard...', event);

    event.stopPropagation();
    const targetId = (event.currentTarget as HTMLElement)?.id;
    const index = targetId ? Number(targetId.replace('card_index_', '')) : NaN;

    if (isNaN(index) || selectedCard?.index === index) return;

    setActiveTab('Cards');

    setSelectedCard(
      selectedCard?.index === index
        ? null
        : {
            index,
            obj: documentSection?.cards[index] as IPromptCard,
          },
    );
  };

  const updateWordCount = () => {
    const countWords = (text: string) => {
      if (!text) return 0;
      return text.trim().split(/\s+/).length;
    };

    let totalWordCount = 0;

    const children = documentRef.current?.children || [];
    for (let i = 0; i < children.length; ++i) {
      try {
        totalWordCount += countWords(children[i].textContent);
      } catch (e) {}
    }

    if (wordCount !== totalWordCount) setWordCount(totalWordCount);
  };

  useEffect(() => {
    console.log('::::inside useEffect documentRef...');
    const observer = new MutationObserver(updateWordCount);

    if (documentRef.current) {
      observer.observe(documentRef.current, { childList: true, subtree: true });
    }

    updateWordCount();

    return () => {
      if (observer) observer.disconnect();
    };
  }, [documentRef]);

  const [generateWholeDocument, setGenerateWholeDocument] =
    useState<boolean>(false);

  useEffect(() => {
    if (
      isGeneratingResponse ||
      !generateWholeDocument ||
      !documentSection?.cards
    )
      return;

    const index = documentSection?.cards.findIndex(
      (item) =>
        item.type === 'Prompt' && item.response === '' && item.include === true,
    );

    if (index !== undefined && index > -1) {
      (documentSection.cards[index] as IPromptCard).auto = true;

      setSelectedCard({
        index: index,
        obj: documentSection.cards[index],
      });
    } else {
      setSelectedCard(null);
      setGenerateWholeDocument(false);
    }
  }, [isGeneratingResponse, generateWholeDocument]);

  useEffect(() => {
    if (selectedCard?.obj.type !== 'Prompt') return;

    const card = selectedCard.obj as IPromptCard;
    if (!selectedCard.obj.auto) return;

    card.auto = false;
    setRegenerateIndex(selectedCard.index);
  }, [selectedCard]);

  const handleTitleEdit = (_index: number, newTitle: string) => {
    if (!documentSection?.cards) return;

    const sort = documentSection.cards[_index].sort;
    documentSection.cards.forEach((card, idx) => {
      if (card.sort === sort) {
        documentSection.cards[idx].title = newTitle;
      }
    });

    console.log('::::setDocument section 14...');
    setDocumentSection({ ...documentSection });
    setEditingTitleIndex(null);
    setSaveEnabled(true);
  };

  console.log(
    ':::::render DocumentRenderer documentSection...',
    documentSection,
  );

  return (
    <div id="document-renderer" ref={documentRef} style={styles.documentRef}>
      {!showGenerateReportPanel &&
        documentSection?.cards
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => item.include)
          .sort((a, b) => a.item.sort - b.item.sort)
          .map(({ item: card, index }) => {
            if (documentSection?.cards[index].type === 'TextBlock') {
              return (
                <SectionBlock
                  key={card.id || index}
                  title={''}
                  index={index}
                  handleTitleEdit={handleTitleEdit}
                  editingTitleIndex={editingTitleIndex}
                  setEditingTitleIndex={setEditingTitleIndex}
                  selectedCard={selectedCard}
                  handleSelectCard={handleSelectCard}
                  styles={styles}>
                  <TextBlock
                    key={card.id || index}
                    index={index}
                    textCard={card as ITextBlock}
                    enableQuill={selectedCard?.index === index}
                    quillRef={quillRef}
                    setSaveEnabled={setSaveEnabled}
                    booking={booking}
                    description={description || ''}
                  />
                </SectionBlock>
              );
            } else if (documentSection?.cards[index].type === 'Checklist') {
              return (
                <SectionBlock
                  key={card.id || index}
                  title={card.title}
                  index={index}
                  handleTitleEdit={handleTitleEdit}
                  editingTitleIndex={editingTitleIndex}
                  setEditingTitleIndex={setEditingTitleIndex}
                  selectedCard={selectedCard}
                  handleSelectCard={handleSelectCard}
                  styles={styles}>
                  <ChecklistBlock
                    checklistCard={card as IChecklist}
                    setSaveEnabled={setSaveEnabled}
                    highlightedSids={highlightedSids}
                    setHighlightedSids={setHighlightedSids}
                    setHighlightSentence={setHighlightSentence}
                  />
                </SectionBlock>
              );
            } else if (documentSection?.cards[index].type === 'Form') {
              return (
                <SectionBlock
                  key={card.id || index}
                  title={card.title}
                  index={index}
                  handleTitleEdit={handleTitleEdit}
                  editingTitleIndex={editingTitleIndex}
                  setEditingTitleIndex={setEditingTitleIndex}
                  selectedCard={selectedCard}
                  handleSelectCard={handleSelectCard}
                  styles={styles}>
                  <FormBlock
                    key={card.id || index}
                    index={index}
                    itemKey={`form-${card.id || index}`}
                    styles={styles}
                    formCard={card as IForm}
                    documentSection={documentSection}
                    setDocumentSection={setDocumentSection}
                    setSaveEnabled={setSaveEnabled}
                    highlightedSids={highlightedSids}
                    setHighlightedSids={setHighlightedSids}
                    setHighlightSentence={setHighlightSentence}
                  />
                </SectionBlock>
              );
            } else {
              return (
                <SectionBlock
                  key={card.id || index}
                  title={card.title}
                  index={index}
                  handleTitleEdit={handleTitleEdit}
                  editingTitleIndex={editingTitleIndex}
                  setEditingTitleIndex={setEditingTitleIndex}
                  selectedCard={selectedCard}
                  handleSelectCard={handleSelectCard}
                  styles={styles}>
                  <PromptBlock
                    key={card.id || index}
                    index={index}
                    styles={styles}
                    promptCard={card as IPromptCard}
                    documentSection={documentSection}
                    setDocumentSection={setDocumentSection}
                    setSelectedCard={setSelectedCard}
                    setSelectedImage={setSelectedImage}
                    enableQuill={selectedCard?.index === index}
                    quillRef={quillRef}
                    setSaveEnabled={setSaveEnabled}
                    imagesMapRef={imagesMapRef?.current}
                    highlightedSids={highlightedSids}
                    setHighlightedSids={setHighlightedSids}
                    regenerateIndex={regenerateIndex}
                  />
                </SectionBlock>
              );
            }
          })}
      {showGenerateReportPanel && (
        <GenerateReport
          characterLimit={characterLimit}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          documentSection={documentSection}
          documentsRef={documentsRef}
          documentIndex={documentIndex}
          setDocumentSection={setDocumentSection}
          setDocumentSections={setDocumentSections}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setSaveEnabled={setSaveEnabled}
          setGenerateWholeDocument={setGenerateWholeDocument}
        />
      )}
    </div>
  );
};

export default DocumentRenderer;
